import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { downloadIcon } from '@progress/kendo-svg-icons';
import { Loader } from '@progress/kendo-react-indicators';
import {
  CpsDocumentMetadata,
  CpsField,
  DocumentVersionResponse,
  MainDocContentTypeResponse
} from '@cs-cms/types';
import {
  DisplayableMetadataEntry,
  formatDateForDisplay,
  getDisplayableMetadata
} from './documentVersionViewerHelpers';

interface DocumentVersionViewerHeaderProps {
  documentName: string;
  documentId: string;
  translate: (key: string) => string;
}

export const DocumentVersionViewerHeader: React.FC<DocumentVersionViewerHeaderProps> = ({
  documentName,
  documentId,
  translate
}) => (
  <div className="headerSection">
    <h2>
      {translate('grid.versionViewer.versionHistoryTitle')}
      {documentName}
    </h2>
    <p>
      {translate('grid.versionViewer.documentIdLabel')}
      {documentId}
    </p>
  </div>
);

interface VersionListEmptyStateProps {
  message: string;
}

const VersionListEmptyState: React.FC<VersionListEmptyStateProps> = ({ message }) => (
  <div className="emptyState">
    <p>{message}</p>
  </div>
);

interface VersionCardProps {
  version: DocumentVersionResponse;
  isSelected: boolean;
  isLoadingMetadata: boolean;
  documentName: string;
  downloadUrl: string | undefined;
  onSelect: (version: DocumentVersionResponse) => void;
}

const VersionCard: React.FC<VersionCardProps> = ({
  version,
  isSelected,
  isLoadingMetadata,
  documentName,
  downloadUrl,
  onSelect
}) => {
  const cardClassName = ['versionCard', isSelected ? 'selected' : '', isSelected && isLoadingMetadata ? 'loading' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div key={version.versionId} className={cardClassName} onClick={() => onSelect(version)}>
      <div className="versionCardHeader">
        <div className="versionTitle">
          <h4>{formatDateForDisplay(version.versionId)}</h4>
          {isSelected && isLoadingMetadata && <Loader size="small" type="pulsing" />}
        </div>
        <div>
          <a
            href={downloadUrl}
            download={`${documentName}_${version.versionId}`}
            onClick={e => e.stopPropagation()}
          >
            <Button size="small" fillMode="flat" svgIcon={downloadIcon} />
          </a>
        </div>
      </div>
    </div>
  );
};

interface VersionListPanelProps {
  versions: DocumentVersionResponse[];
  isLoading: boolean;
  error: unknown;
  selectedVersion: DocumentVersionResponse | null;
  isLoadingMetadata: boolean;
  documentName: string;
  getDownloadUrl: (version: DocumentVersionResponse) => string | undefined;
  onSelectVersion: (version: DocumentVersionResponse) => void;
  translate: (key: string) => string;
}

export const VersionListPanel: React.FC<VersionListPanelProps> = ({
  versions,
  isLoading,
  error,
  selectedVersion,
  isLoadingMetadata,
  documentName,
  getDownloadUrl,
  onSelectVersion,
  translate
}) => {
  const renderVersionListContent = (): React.ReactNode => {
    if (isLoading) {
      return <VersionListEmptyState message={translate('grid.versionViewer.loadingVersionsMessage')} />;
    }

    if (error) {
      return <VersionListEmptyState message={translate('grid.versionViewer.errorLoadingVersionsMessage')} />;
    }

    if (versions.length === 0) {
      return <VersionListEmptyState message={translate('grid.versionViewer.noVersionsFoundMessage')} />;
    }

    return versions.map(version => (
      <VersionCard
        key={version.versionId}
        version={version}
        isSelected={selectedVersion?.versionId === version.versionId}
        isLoadingMetadata={isLoadingMetadata}
        documentName={documentName}
        downloadUrl={getDownloadUrl(version)}
        onSelect={onSelectVersion}
      />
    ));
  };

  return (
    <div className="leftPanel">
      <div className="leftPanelHeader">
        <h3>{translate('grid.versionViewer.availableVersionsTitle')}</h3>
        <p>
          {versions.length}
          {translate('grid.versionViewer.versionsFoundSuffix')}
        </p>
      </div>
      <div className="versionList">{renderVersionListContent()}</div>
    </div>
  );
};

interface VersionMetadataSectionProps {
  metadataEntries: DisplayableMetadataEntry[];
  translate: (key: string) => string;
}

const VersionMetadataSection: React.FC<VersionMetadataSectionProps> = ({ metadataEntries, translate }) => (
  <div className="metadataSection">
    <h4>{translate('grid.versionViewer.metadataTitle')}</h4>
    <div className="detailsGrid">
      {metadataEntries.map(({ key, displayName, value }) => (
        <div key={key}>
          <span title={key}>{displayName}: </span>
          <span>{value}</span>
        </div>
      ))}
    </div>
  </div>
);

interface VersionDetailsPanelProps {
  selectedVersion: DocumentVersionResponse | null;
  versionMetadata: CpsDocumentMetadata | undefined;
  isLoadingMetadata: boolean;
  documentName: string;
  allFields: CpsField[] | undefined;
  contentTypeData: MainDocContentTypeResponse | undefined;
  getDownloadUrl: (version: DocumentVersionResponse) => string | undefined;
  translate: (key: string) => string;
}

export const VersionDetailsPanel: React.FC<VersionDetailsPanelProps> = ({
  selectedVersion,
  versionMetadata,
  isLoadingMetadata,
  documentName,
  allFields,
  contentTypeData,
  getDownloadUrl,
  translate
}) => {
  if (!selectedVersion) {
    return (
      <div className="rightPanel">
        <div className="emptyState">
          <p>{translate('grid.versionViewer.selectVersionMessage')}</p>
        </div>
      </div>
    );
  }

  if (isLoadingMetadata) {
    return (
      <div className="rightPanel">
        <div className="loadingState">
          <Loader size="medium" type="converging-spinner" />
          <p>{translate('grid.versionViewer.loadingMetadataMessage')}</p>
        </div>
      </div>
    );
  }

  const metadataEntries = getDisplayableMetadata(versionMetadata, allFields, contentTypeData);

  return (
    <div className="rightPanel">
      <div className="rightPanelHeader">
        {metadataEntries.length > 0 ? <VersionMetadataSection metadataEntries={metadataEntries} translate={translate} /> : null}
      </div>
      <div className="downloadSection">
        <p>{translate('grid.versionViewer.downloadInstructionMessage')}</p>
        <a href={getDownloadUrl(selectedVersion)} download={`${documentName}_${selectedVersion.versionId}`}>
          <Button size="large" themeColor="primary" svgIcon={downloadIcon}>
            {translate('grid.versionViewer.downloadVersionButtonText')}
          </Button>
        </a>
      </div>
    </div>
  );
};
